/**
 * useArchiveMode — the "archive mode": on the map, one tap on a route archives
 * it.
 *
 * Why it exists: stripping a wall means archiving 20–40 routes, and doing that
 * one route modal at a time (open → scroll to the edit expander → Archive →
 * close) is the slowest routine a setter has. On the map every route is already
 * in front of them, in its real position on the wall.
 *
 * It borrows the quick-log mode's shape, but archiving is a destructive act, so
 * it is deliberately more restrictive:
 *   - **disarmed by leaving the map** — the toggle and the red banner both live
 *     on the map, so anywhere else the mode would be armed with nothing on
 *     screen saying so,
 *   - **not persisted** (quick-log survives a PWA restart via sessionStorage;
 *     this must not — coming back to a map where a tap silently archives is
 *     exactly the failure this rule prevents),
 *   - **one undo toast at a time** — quick-log suppresses per-tap toasts
 *     entirely (25 laps would mean 25 toasts). Here the undo is worth keeping,
 *     but stripping a wall is a fast burst of taps, so each toast REPLACES the
 *     previous one instead of stacking: the map stays visible, and the route
 *     just archived — the only one a mis-tap can still be about — always has a
 *     way back,
 *   - **shorter idle timeout** than quick-log's three hours.
 *
 * Capability: `routes.archive`. The caller passes `canArchive`; losing it
 * mid-session (role change) disarms the mode rather than leaving a live tap
 * handler behind a check that no longer passes.
 */
import { useState, useEffect, useCallback, useRef } from "react";

/** Disarm after this long without a tap. Far shorter than quick-log's window:
 *  an armed archive map left open is a hazard, an armed log map is not. */
export const ARCHIVE_MODE_IDLE_MS = 10 * 60 * 1000;

/**
 * @param {object}   opts
 * @param {boolean}  opts.canArchive     `can('routes.archive')`
 * @param {boolean}  opts.blocked        route placing / reposition in progress
 * @param {Function} opts.setRouteActive (routeId, active) => Promise
 * @param {object}   opts.toast          useToast()
 * @param {Function} opts.t              i18n
 */
export function useArchiveMode({ canArchive, blocked, setRouteActive, toast, t }) {
  const [active, setActive] = useState(false);
  // Routes archived in this run, newest first — what the bar counts.
  const [archived, setArchived] = useState([]);
  const idleTimerRef = useRef(null);
  // The mode owns exactly ONE toast slot. Every toast it raises goes through
  // `slotToast`, so a burst of taps can never bury the map it is aimed at.
  const toastIdRef = useRef(null);

  /** Show a toast in the mode's single slot, dropping whatever was there. */
  const slotToast = useCallback((opts) => {
    if (toastIdRef.current) toast.dismiss(toastIdRef.current);
    toastIdRef.current = toast.push(opts) || null;
  }, [toast]);

  const stop = useCallback(() => {
    setActive(false);
    setArchived([]);
    // The last undo toast deliberately survives "Done": ending the mode is not
    // a reason to take back the way out of the tap that ended it.
  }, []);

  // Placing a route redefines what tapping the map does, and losing the
  // capability must not leave an armed map behind.
  useEffect(() => {
    if ((blocked || !canArchive) && active) stop();
  }, [blocked, canArchive, active, stop]);

  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => stop(), ARCHIVE_MODE_IDLE_MS);
  }, [stop]);

  useEffect(() => {
    if (!active) {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
      return undefined;
    }
    armIdleTimer();
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); };
  }, [active, armIdleTimer]);

  const start = useCallback(() => {
    if (blocked || !canArchive) return;
    setActive(true);
    setArchived([]);
  }, [blocked, canArchive]);

  const toggle = useCallback(() => { if (active) stop(); else start(); }, [active, start, stop]);

  /**
   * Archive one route from a tap. Returns true when the tap was consumed, so
   * the caller knows not to fall through to opening the route modal.
   */
  const tapRoute = useCallback(async (route) => {
    if (!active || !route || blocked || !canArchive) return false;
    armIdleTimer();
    // Already archived (an archived route can still be on screen while the
    // listener catches up). Say so instead of writing a no-op.
    if (route.active === false) {
      slotToast({ text: t('archiveMode.alreadyArchived'), durationMs: 2000 });
      return true;
    }
    const name = route.name || t('route.ungraded');
    try {
      await setRouteActive(route.id, false);
    } catch (e) {
      console.error("Archive failed", e);
      slotToast({ text: t('archiveMode.failed', { name }), durationMs: 5000 });
      return true;
    }
    setArchived(prev => [{ id: route.id, name }, ...prev]);
    slotToast({
      text: t('archiveMode.archived', { name }),
      undoLabel: t('toast.undo'),
      durationMs: 6000,
      onUndo: async () => {
        // The toast row closes itself right after this, so the slot is free.
        toastIdRef.current = null;
        try {
          await setRouteActive(route.id, true);
          setArchived(prev => prev.filter(r => r.id !== route.id));
        } catch (e) { console.error("Un-archive failed", e); }
      },
    });
    return true;
  }, [active, blocked, canArchive, setRouteActive, slotToast, t, armIdleTimer]);

  return { active, start, stop, toggle, tapRoute, archived };
}
